import { GetBasketHoldParamType } from "@app-contexts/user/features/basket/data/types/basket";
import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import UpgradeOffer from "@app-contexts/public/features/basket/components/cart-summary/cart-summary-items/UpgradeOffer/UpgradeOffer.web";
import { useItemGroupUpgrade } from "@app-contexts/public/features/basket/hooks/useItemGroupUpgrade";
import usePublicEventsService from "@app-contexts/public/features/event-detail/data/services/usePublicEventsService";
import usePublicProductsService from "@app-contexts/public/features/products/data/services/usePublicProductsService";
import PublicAssignedSeatingModal from "@app-contexts/public/features/seating/components/PublicAssignedSeatingModal/PublicAssignedSeatingModal.web";
import { useCheckoutTrackingContext } from "@app-contexts/public/shared/checkout/contexts/useCheckoutTrackingContext";
import { useReviewItemGroupHoldState } from "@app-contexts/public/shared/checkout/hooks/useReviewItemGroupHoldState";
import { useDisclosure } from "@shared/hooks/useDisclosure";
import ItemGroup from "@shared/components/application/data-display/item-group/ItemGroup.web";
import AppliedDiscounts from "@shared/modules/basket/components/AppliedDiscounts/AppliedDiscounts.web";
import { useBasketService } from "@shared/modules/basket/context/BasketServiceContext";
import { TicketItemGroupType } from "@shared/modules/item-groups/types/ticket-item-group-type";
import usePublicMembershipGroupsService from "@shared/modules/memberships/data/services/usePublicMembershipGroupsService";
import RemoveAllSeatsModal from "@shared/modules/seating/components/RemoveAllSeatsModal/RemoveAllSeatsModal.web";
import { hasSeating } from "@shared/utils/seating/utils";
import ItemTypeTag from "@ui-application/data-display/ItemTypeTag/ItemTypeTag.web";
import Button from "@ui-kit/form/Button/Button.web";
import Tag from "@ui-kit/data-display/Tag/Tag.web";
import Icon from "@ui-kit/icons/Icon.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Menu from "@ui-kit/overlays/Menu/Menu.web";
import Modal from "@ui-kit/overlays/Modal/Modal.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { useTranslation } from "@shared/utils/internationalization";
import { useWillCallLabel } from "@shared/modules/branding/hooks/useWillCallLabel";

import { getTicketTypes } from "../../../add-ons/EventAddons/helpers";
import useReviewItemGroup from "./useReviewItemGroup";

export type ReviewItemGroupProps = {
  itemGroup: TicketItemGroupType;
  allowQuantityChange: boolean;
  dynamicHoldParams?: GetBasketHoldParamType;
};

function ReviewItemGroup({
  itemGroup,
  allowQuantityChange = true,
  dynamicHoldParams,
}: ReviewItemGroupProps): JSX.Element {
  const { t } = useTranslation("common");
  const willCallLabel = useWillCallLabel();
  const contextBasket = useBasketService?.();
  const publicBasket = usePublicBasket();
  const basketToUse = contextBasket || publicBasket;
  const { currency, isGroupSale, holdSlug } = basketToUse;
  const { basketUpgrades } = publicBasket;
  const upgradeInfo =
    !holdSlug && !dynamicHoldParams && itemGroup.id != null
      ? basketUpgrades?.getBasketUpgradeForItemGroupId(itemGroup.id)
      : undefined;
  const firstUpgradeOption = upgradeInfo?.upgradeOptions?.[0];
  const { handleUpgrade } = useItemGroupUpgrade();

  const {
    isOpen: isSeatingModalOpen,
    onOpen: onOpenSeatingModal,
    onClose: onCloseSeatingModal,
  } = useDisclosure({ defaultIsOpen: false });

  const {
    effectiveAllowQuantityChange,
    holdMinQuantity,
    holdMaxQuantity,
    handleOpenAssignedSeatingModal,
    handleCloseAssignedSeatingModal,
  } = useReviewItemGroupHoldState({
    holdSlug,
    dynamicHoldParams,
    itemGroup,
    allowQuantityChange,
    onOpenSeatingModal,
    onCloseSeatingModal,
  });

  const {
    itemGroupDisplay,
    itemGroupActions,
    seatingItemGroupActions,
    seatingItemGroupDisplay,
  } = useReviewItemGroup(itemGroup);

  const {
    onOpenRemoveModal,
    onCloseRemoveModal,
    handleRemove,
    handleChangeQuantity,
  } = itemGroupActions;

  const {
    isRemoveModalOpen,
    shouldDisplay,
    isBasketUpdating,
    subItems,
    subItemsQuantity,
    priceAdjustments,
  } = itemGroupDisplay;

  const {
    handleRemoveAssignedSeat,
    handleResolveSeatingModal,
    handleCloseSeatingModal,
  } = seatingItemGroupActions;
  const { removeSeatModalOpen, removeAllSeatsData } = seatingItemGroupDisplay;

  const { updateItemTracking } = useCheckoutTrackingContext();

  const { showNewItemPrice, hasCredit, discountText, hasDiscountToShow } =
    priceAdjustments;

  const adjustmentLabels =
    hasDiscountToShow || hasCredit ? (
      <Flex direction="row" gap="xs" alignItems="flex-end">
        {hasDiscountToShow && (
          <AppliedDiscounts.Item
            discount={discountText}
            discountsApplied={itemGroup.discounts_applied}
            data-testid="applied-discount-code"
          />
        )}
        {hasCredit && (
          <AppliedDiscounts.Credit
            creditType={itemGroup.credit_type}
            data-testid="applied-credit"
          />
        )}
      </Flex>
    ) : null;

  const hasSeats = hasSeating(itemGroup);
  const isRenewalItemGroup = Boolean(itemGroup.renewal_member);
  const shouldShowRemoveButton =
    effectiveAllowQuantityChange || isRenewalItemGroup;

  if (!shouldDisplay) return <></>;
  return (
    <>
      {/* TODO: Add ticket package display */}
      <ItemGroup
        isBasketUpdating={isBasketUpdating}
        itemGroup={itemGroup}
        handleChangeQuantity={handleChangeQuantity}
        handleRemoveAssignedSeat={handleRemoveAssignedSeat}
        updateItemTracking={updateItemTracking}
        packageChildItems={subItems}
        packageChildQuantity={subItemsQuantity}
        allowQuantityChange={effectiveAllowQuantityChange}
        minQuantityOverride={holdMinQuantity}
        maxQuantityOverride={holdMaxQuantity}
        eventService={usePublicEventsService}
        membershipService={usePublicMembershipGroupsService}
        productService={usePublicProductsService}
        currency={currency}
        header={
          <Flex mb="md" justify="space-between">
            {itemGroup.item_type &&
              (isGroupSale ? (
                <Tag colorPalette="purple" paddingY="2xs" paddingX="xs">
                  {t("Group sale")}
                </Tag>
              ) : (
                <ItemTypeTag
                  itemType={itemGroup.item_type}
                  shippingType={itemGroup.shipping_type}
                  willCallLabel={willCallLabel}
                />
              ))}
            <Box display={{ base: "none", sm: "block" }}>
              {effectiveAllowQuantityChange && hasSeats && (
                <Button.Ghost
                  data-testid={`checkout-edit-seat-${itemGroup.id}`}
                  onClick={handleOpenAssignedSeatingModal}
                  leftIcon={<Icon.Edit.Solid boxSize="element.lg" />}
                  px="2xs"
                >
                  {t("Edit")}
                </Button.Ghost>
              )}

              {shouldShowRemoveButton && (
                <Button.Ghost
                  data-testid={`checkout-remove-${itemGroup.id}`}
                  onClick={onOpenRemoveModal}
                  isDisabled={isBasketUpdating}
                  leftIcon={<Icon.Trash.Outline boxSize="element.lg" />}
                  px="2xs"
                >
                  {t("Remove")}
                </Button.Ghost>
              )}
            </Box>

            {shouldShowRemoveButton && (
              <Box display={{ base: "block", sm: "none" }}>
                <Menu>
                  <Menu.Button
                    data-testid="review-meatball-menu"
                    height="element.lg"
                  >
                    <Icon.MeatballHorizontal.Solid />
                  </Menu.Button>
                  <Menu.List>
                    {effectiveAllowQuantityChange && hasSeats && (
                      <Menu.Item
                        onSelect={handleOpenAssignedSeatingModal}
                        value="edit"
                      >
                        <Icon.Edit.Solid h="element.lg" w="element.lg" />
                        {t("Edit")}
                      </Menu.Item>
                    )}
                    <Menu.Item onSelect={onOpenRemoveModal} value="remove">
                      <Icon.Trash.Outline h="element.lg" w="element.lg" />
                      {t("Remove")}
                    </Menu.Item>
                  </Menu.List>
                </Menu>
              </Box>
            )}
          </Flex>
        }
        adjustmentLabels={adjustmentLabels}
        showNewItemPrice={showNewItemPrice}
      >
        {firstUpgradeOption && upgradeInfo && !itemGroup.is_upgraded && (
          <Box mt="md">
            <UpgradeOffer
              itemType={upgradeInfo.itemType}
              name={firstUpgradeOption.name}
              upgradeAmount={firstUpgradeOption.priceDelta}
              messageTemplate={firstUpgradeOption.messageTemplate}
              dataTestId={`upgrade-offer-${itemGroup.id}`}
              onUpgrade={() => handleUpgrade(itemGroup, upgradeInfo)}
              isLoading={isBasketUpdating}
            />
          </Box>
        )}
      </ItemGroup>

      <Modal isOpen={isRemoveModalOpen} onClose={onCloseRemoveModal} isCentered>
        <Modal.Header>{t("Remove items from cart?")}</Modal.Header>

        <Modal.Body>
          <Text>{`${itemGroup.quantity}x ${itemGroup.description}`}</Text>
        </Modal.Body>

        <Modal.Footer>
          <Button.Solid
            onClick={handleRemove}
            isLoading={isBasketUpdating}
            data-testid="basket-empty-confirm"
          >
            {t("Remove")}
          </Button.Solid>

          <Button.Link
            onClick={onCloseRemoveModal}
            data-testid="basket-empty-modal-close"
            color="font.dark"
          >
            {t("Close", { ns: "common" })}
          </Button.Link>
        </Modal.Footer>
      </Modal>

      {removeAllSeatsData && removeSeatModalOpen && (
        <RemoveAllSeatsModal
          isOpen={removeSeatModalOpen}
          onClose={handleCloseSeatingModal}
          resolve={handleResolveSeatingModal}
          eventData={removeAllSeatsData.eventData}
          $data={removeAllSeatsData.$data}
        />
      )}

      {isSeatingModalOpen && itemGroup.venue_id && (
        <PublicAssignedSeatingModal
          isOpen
          onClose={handleCloseAssignedSeatingModal}
          eventId={itemGroup.event}
          membershipGroupId={itemGroup.membership_group}
          venueId={itemGroup.venue_id}
          ticketTypeFilter={getTicketTypes}
        />
      )}
    </>
  );
}

export default ReviewItemGroup;
